<template>
  <a-layout class="main-layout" has-sider>
    <a-layout
      class="main-layout-content-wrapper"
      :style="{ zIndex: 1, width: '100%' }"
    >
      <a-layout-header
        class="fixed main-layout-header flex items-center border-gray-400 border-b"
      >
        <div class="logo">
          <img class="mx-auto py-3" src="@/assets/images/logo.svg" alt="App Logo">
        </div>

        <TopMenu :menu-items="menuItems" class="mr-auto" />

        <template v-if="$experimental && !envLoading && !authLoading">
          <div class="ml-auto">
            <LangSwitcher v-if="isMaster" class="mr-4" />

            <EnvSwitcher />
          </div>

          <router-link
            class="ml-6 mr-8 flex items-center text-gray-400"
            :to="{ name: 'environments' }"
          >
            <a-icon type="setting" theme="filled" />
          </router-link>
        </template>

        <a-dropdown v-if="!authLoading && user" class="mr-8">
          <a class="ant-dropdown-link" href="#">
            <img
              v-if="user.picture"
              :src="user.picture"
              class="w-8 rounded-full"
            >
            <a-avatar v-else icon="user" />
          </a>
          <a-menu slot="overlay">
            <a-menu-item @click="logout">
              Logout
            </a-menu-item>
          </a-menu>
        </a-dropdown>
      </a-layout-header>
      <a-layout v-if="authLoading || !isEnvLoaded">
        <a-spin class="block mx-auto mb-2 mt-4" />
      </a-layout>
      <a-layout v-else>
        <a-affix
          class="overflow-hidden"
          @change="isSecondaryBarSticky = $event"
        >
          <a-row
            v-if="showSecondaryBar"
            :gutter="16"
            align="middle"
            type="flex"
            class="bg-gray-300 border-b border-gray-400"
          >
            <a-col :span="9" class="flex items-center">
              <NavigateBackButton class="mx-6" />

              <Breadcrumb />

              <PortalTarget name="breadcrumb-suffix" />
            </a-col>

            <a-col :span="6">
              <PortalTarget name="page-nav" />
            </a-col>

            <a-col :span="9" class="flex">
              <div class="ml-auto flex">
                <LangSwitcher
                  v-if="isMaster"
                  v-show="isSecondaryBarSticky"
                  theme="light"
                  class="mr-4"
                />
                <EnvSwitcher
                  v-show="isSecondaryBarSticky"
                  class="mr-2"
                  theme="light"
                />
                <PortalTarget
                  class="mr-6 whitespace-no-wrap"
                  name="page-actions"
                  multiple
                />
              </div>
            </a-col>
          </a-row>
          <PortalTarget name="page-top-banner" />
        </a-affix>
        <a-layout-content
          class="px-8 pt-6 pb-10 m-0"
          :style="{ minHeight: '280px', flexGrow: 'initial' }"
        >
          <slot />
        </a-layout-content>
      </a-layout>
      <a-layout-footer class="text-center">
        © 2022, EKI, Alle rechten voorbehouden
      </a-layout-footer>
    </a-layout>
  </a-layout>
</template>

<script lang="ts">
import Breadcrumb from './Breadcrumb.vue'
import NavigateBackButton from './NavigateBackButton.vue'
import TopMenu from './TopMenu.vue'
import EnvSwitcher from './EnvSwitcher.vue'
import LangSwitcher from './LangSwitcher.vue'
import useBreadcrumb from '@/composables/useBreadcrumb'
import { defineComponent, onMounted, ref } from '@vue/composition-api'
import { useAuth } from '@/lib/auth'
import { useEnv } from '@/composables/useEnvironment'

export default defineComponent({
  name: 'Layout',

  components: {
    Breadcrumb,
    TopMenu,
    EnvSwitcher,
    LangSwitcher,
    NavigateBackButton
  },

  props: {
    menuItems: {
      type: Array,
      required: true
    }
  },

  setup(_, { root }) {
    const { showBreadcrumb } = useBreadcrumb()
    const { currentEnv, loading: envLoading, isEnvLoaded, isMaster } = useEnv()

    onMounted(() => {
      root.$message.config({
        getContainer: () =>
          document.querySelector('.main-layout-header') ||
          // eslint-disable-next-line
          document.querySelector('body')!
      })
    })

    const isSecondaryBarSticky = ref(false)

    const { logout, user, loading: authLoading } = useAuth()

    return {
      logout,
      user,
      authLoading,
      showSecondaryBar: showBreadcrumb,
      isSecondaryBarSticky,

      isMaster,
      currentEnv,
      envLoading,
      isEnvLoaded
    }
  }
})
</script>

<style lang="scss">
.main-layout {
  height: 100%;

  .trigger {
    padding: 0 24px;
    font-size: 1.2rem;
    line-height: 4rem;
    cursor: pointer;
    transition: color 0.3s;
  }

  .trigger:hover {
    color: #1890ff;
  }

  .logo {
    display: flex;
    height: 100%;
    margin-right: 2rem;
    padding: 0 2rem;
    text-align: center;
    background: #001529;
  }

  .main-layout-header {
    position: relative;
    padding: 0;
    background: #001529;
  }

  .main-layout-content-wrapper {
    transition: margin 0.15s ease-in;
  }

  .main-layout-content {
    min-height: 40rem;
    margin: 1.5rem 1rem;
    padding: 1.5rem;
    background: #fff;
  }

  .main-layout-aside {
    position: fixed;
    left: 0;
    height: 100vh;
    overflow: auto;
  }
  .overflow-hidden .ant-affix {
    overflow: hidden;
  }
}
</style>
